/**
 * SettingsFocusTimeSection
 *
 * Focus time settings: enable, preferred time of day, block duration,
 * days per week, preferred days/hours, calendar, color, and action buttons.
 */

import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Target } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import type { CalendarSettings } from '../types';
import type {
  FocusTimeSettingsDetail,
  FocusTimeSettingsInput,
  CalendarCollectionDetail,
} from '@/lib/api';
import { Section, ToggleRow, Field, formatHour, HOURS, DAY_LABELS } from './settings-helpers';

interface SettingsFocusTimeSectionProps {
  settings: CalendarSettings;
  focusTimeSettings?: FocusTimeSettingsDetail | null;
  onUpdateFocusTime?: (input: FocusTimeSettingsInput) => Promise<unknown>;
  onGenerateFocusBlocks?: () => Promise<number>;
  onRegenerateFocusBlocks?: () => Promise<number>;
  onClearFocusBlocks?: () => Promise<number>;
  calendars?: CalendarCollectionDetail[];
}

export function SettingsFocusTimeSection({
  settings,
  focusTimeSettings,
  onUpdateFocusTime,
  onGenerateFocusBlocks,
  onRegenerateFocusBlocks,
  onClearFocusBlocks,
  calendars,
}: SettingsFocusTimeSectionProps) {
  const [focusLoading, setFocusLoading] = useState(false);
  const tf = settings.timeFormat ?? '12h';

  const handleFocusAction = async (action: () => Promise<number>, label: string) => {
    setFocusLoading(true);
    try {
      const count = await action();
      toast({ title: `${label}: ${count} blocks` });
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : `Failed to ${label.toLowerCase()}`,
        variant: 'destructive',
      });
    } finally {
      setFocusLoading(false);
    }
  };

  return (
    <Section
      icon={Target}
      title="Focus Time"
      description="Automatically block distraction-free time around existing events."
    >
      {onUpdateFocusTime ? (
        <>
          <ToggleRow
            label="Enable focus time blocks"
            checked={focusTimeSettings?.isEnabled ?? false}
            onCheckedChange={(checked) => onUpdateFocusTime({ isEnabled: checked })}
          />

          <Field label="Preferred time of day">
            <Select
              value={focusTimeSettings?.preferredTimeOfDay ?? 'morning'}
              onValueChange={(v) =>
                onUpdateFocusTime({
                  preferredTimeOfDay: v as 'morning' | 'afternoon' | 'evening',
                })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="morning">Morning</SelectItem>
                <SelectItem value="afternoon">Afternoon</SelectItem>
                <SelectItem value="evening">Evening</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field label="Block duration (minutes)">
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={15}
                value={focusTimeSettings?.minBlockMinutes ?? 60}
                onChange={(e) =>
                  onUpdateFocusTime({
                    minBlockMinutes: parseInt(e.target.value, 10) || 60,
                  })
                }
                className="w-20"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <Input
                type="number"
                min={15}
                value={focusTimeSettings?.maxBlockMinutes ?? 120}
                onChange={(e) =>
                  onUpdateFocusTime({
                    maxBlockMinutes: parseInt(e.target.value, 10) || 120,
                  })
                }
                className="w-20"
              />
            </div>
          </Field>

          <Field label="Days per week">
            <Input
              type="number"
              min={1}
              max={7}
              value={focusTimeSettings?.daysPerWeek ?? 3}
              onChange={(e) =>
                onUpdateFocusTime({
                  daysPerWeek: parseInt(e.target.value, 10) || 3,
                })
              }
              className="w-20"
            />
          </Field>

          <div>
            <Label className="text-[13px] mb-1.5 block">Preferred days</Label>
            <div className="flex flex-wrap gap-1.5">
              {DAY_LABELS.map((label, i) => {
                const preferred = focusTimeSettings?.preferredDays ?? [1, 2, 3, 4, 5];
                const isSelected = preferred.includes(i);
                return (
                  <button
                    key={i}
                    type="button"
                    className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
                      isSelected
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'border-input hover:bg-muted'
                    }`}
                    onClick={() => {
                      const next = isSelected
                        ? preferred.filter((d) => d !== i)
                        : [...preferred, i].sort();
                      if (next.length > 0) {
                        onUpdateFocusTime({ preferredDays: next });
                      }
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <Field label="Preferred hours">
            <div className="flex items-center gap-2">
              <Select
                value={String(focusTimeSettings?.preferredStartHour ?? 9)}
                onValueChange={(v) => onUpdateFocusTime({ preferredStartHour: parseInt(v, 10) })}
              >
                <SelectTrigger className="w-[6.5rem]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HOURS.map((h) => (
                    <SelectItem key={h} value={String(h)}>
                      {formatHour(h, tf)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">to</span>
              <Select
                value={String(focusTimeSettings?.preferredEndHour ?? 12)}
                onValueChange={(v) => onUpdateFocusTime({ preferredEndHour: parseInt(v, 10) })}
              >
                <SelectTrigger className="w-[6.5rem]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HOURS.map((h) => (
                    <SelectItem key={h} value={String(h)}>
                      {formatHour(h, tf)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </Field>

          {calendars && calendars.length > 0 && (
            <Field label="Calendar for focus blocks">
              <Select
                value={focusTimeSettings?.calendarId ?? '__default__'}
                onValueChange={(v) =>
                  onUpdateFocusTime({ calendarId: v === '__default__' ? null : v })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Default calendar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__default__">Default calendar</SelectItem>
                  {calendars.map((cal) => (
                    <SelectItem key={cal.id} value={cal.id}>
                      {cal.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}

          <Field label="Focus block color">
            <Input
              type="color"
              value={focusTimeSettings?.color ?? '#8b5cf6'}
              onChange={(e) => onUpdateFocusTime({ color: e.target.value })}
              className="w-16 h-8 p-0.5"
            />
          </Field>

          <div className="flex gap-2 pt-1">
            {onGenerateFocusBlocks && (
              <Button
                size="sm"
                variant="outline"
                disabled={focusLoading}
                onClick={() => handleFocusAction(onGenerateFocusBlocks, 'Generated')}
              >
                {focusLoading && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                Generate
              </Button>
            )}
            {onRegenerateFocusBlocks && (
              <Button
                size="sm"
                variant="outline"
                disabled={focusLoading}
                onClick={() => handleFocusAction(onRegenerateFocusBlocks, 'Regenerated')}
              >
                Regenerate
              </Button>
            )}
            {onClearFocusBlocks && (
              <Button
                size="sm"
                variant="outline"
                disabled={focusLoading}
                onClick={() => handleFocusAction(onClearFocusBlocks, 'Cleared')}
              >
                Clear All
              </Button>
            )}
          </div>
        </>
      ) : (
        <p className="text-[13px] text-muted-foreground py-2">
          Focus time settings are not available.
        </p>
      )}
    </Section>
  );
}
